import React from 'react';
import { motion } from 'framer-motion';

import {
    ArrowRightCircle,
    Heart,
    BookOpen,
    Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import OptimizedImage from '../components/utilities/OptimizedImage';



const AboutPage: React.FC = () => {

    const navigate = useNavigate();
    const journeySteps = [
        {
            icon: <BookOpen size={48} color="#8E44AD" />,
            title: "The Academic Foundation",
            description: "My journey began at TISS, Mumbai, where I discovered the power of understanding human experiences beyond traditional therapeutic boundaries."
        },
        {
            icon: <Heart size={48} color="#E2B010" />,
            title: "Breaking Conventional Paths",
            description: "Instead of following a linear career, I embraced diverse roles - from teaching and counseling to product management - each enriching my understanding of human complexity."
        },
        {
            icon: <Globe size={48} color="#A1ADA5" />,
            title: "Greh Pravesh: A Holistic Vision",
            description: "I realized therapy isn't about fitting into a single mold, but about creating personalized, compassionate spaces for genuine healing and self-discovery."
        }
    ];

    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>About Greh Pravesh Therapy - Meet Our Founder Aanya Wadhwani</title>
                <meta name="description" content="Meet Aanya Wadhwani, TISS Mumbai alumna & founder of India's first no-revenue-sharing therapy practice. Expert counseling & mental health services. Book consultation today." />

                {/* Keywords using approachable terms */}
                <meta name="keywords" content="Aanya Wadhwani therapist, TISS Mumbai counselor, therapy group practice, mental health expert, counseling center Mumbai, wellness counselor, personal growth therapy, Indian therapist, holistic healing, mental health professional, emotional support specialist, relationship counselor" />

                {/* Open Graph Tags */}
                <meta property="og:title" content="About Greh Pravesh Therapy - Redefining Therapeutic Care in India" />
                <meta property="og:description" content="Discover our unique approach to therapy - India's first no-revenue-sharing group practice led by TISS alumna Aanya Wadhwani. Creating compassionate spaces for healing and growth." />
                <meta property="og:type" content="profile" />
                <meta property="og:image" content="/images/i.JPG" />

                {/* Schema.org markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AboutPage",
                        "mainEntity": {
                            "@type": "MedicalOrganization",
                            "name": "Greh Pravesh Therapy",
                            "founder": {
                                "@type": "Person",
                                "name": "Aanya Wadhwani",
                                "jobTitle": "Therapist and Founder",
                                "image": "/images/i.JPG",
                                "alumniOf": {
                                    "@type": "CollegeOrUniversity",
                                    "name": "Tata Institute of Social Sciences",
                                    "location": "Mumbai"
                                },
                                "description": "Mental health professional with diverse experience in counseling, teaching, and product management"
                            },
                            "description": "India's first no-revenue-sharing therapy group practice, creating therapist-centric spaces for optimal client care",
                            "specialty": [
                                "Individual Therapy",
                                "Mental Health Support",
                                "Personal Growth Counseling",
                                "Professional Development"
                            ],
                            "ethicsPolicy": "No-revenue-sharing Group Practice Model",
                            "differentiatingFeatures": [
                                "Therapist-centric approach",
                                "Holistic healing spaces",
                                "Personalized care",
                                "Continuous learning environment"
                            ],
                            "knowsAbout": [
                                "Mental Health Therapy",
                                "Counseling Psychology",
                                "Personal Development",
                                "Professional Growth",
                                "Emotional Wellness"
                            ],
                            "slogan": "Redefining Therapeutic Landscapes",
                            "foundingDate": "", // Add founding date
                            "address": {        // Add address details
                                "@type": "PostalAddress",
                                "addressLocality": "",
                                "addressRegion": "",
                                "addressCountry": "IN"
                            }
                        },
                        "hasPart": [
                            {
                                "@type": "WebPageElement",
                                "name": "The Academic Foundation",
                                "description": "Journey beginning at TISS, Mumbai, exploring human experiences beyond traditional therapeutic boundaries"
                            },
                            {
                                "@type": "WebPageElement",
                                "name": "Breaking Conventional Paths",
                                "description": "Diverse career experience including teaching, counseling, and product management"
                            },
                            {
                                "@type": "WebPageElement",
                                "name": "Greh Pravesh: A Holistic Vision",
                                "description": "Creating personalized, compassionate spaces for genuine healing and self-discovery"
                            }
                        ]
                    })}
                </script>
            </Helmet>
            <div className="bg-white min-h-screen">
                {/* Landing Section */}
                <section className="relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-red-600 to-softwhite">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500 to-red-100/10 rounded-br-[100%] z-0"></div>

                    <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
                        <div className="max-w-2xl w-full space-y-6 md:mt-16">
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="bg-white/20 inline-block px-4 py-2 rounded-full"
                            >
                                <span className="text-softwhite text-sm font-medium">Redefining Therapeutic Landscapes</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                                className="text-2xl md:text-3xl text-softwhite font-bold"
                            >
                                Greh Pravesh Therapy
                            </motion.h1>



                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.2 }}
                                className="text-white text-md max-w-xl mx-auto"
                            >
                                A journey of diverse experiences, continuous learning, and a commitment to creating compassionate, personalized healing spaces.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.5 }}
                                className="pt-8 space-y-6"
                            >
                                <button className="bg-golden text-white flex items-center justify-center mx-auto px-8 py-4 rounded-xl hover:opacity-90 transition-all">
                                    Meet our Founder
                                    <ArrowRightCircle className="ml-3" />
                                </button>
                                <motion.div
                                    className="block md:hidden w-full h-96 md:h-full"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.9, delay: 3 }}
                                >
                                    <OptimizedImage
                                        src="/images/i.JPG"
                                        alt="best psychologist in delhi"
                                        className="w-full h-full object-cover rounded-xl opacity-80"
                                        width={800}
                                        height={600}
                                    />
                                </motion.div>

                                <motion.h1
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                    className="text-2xl md:text-3xl lg:text-4xl text-softwhite font-bold"
                                >
                                    Aanya Wadhwani
                                </motion.h1>
                            </motion.div>
                        </div>
                    </div>

                    <motion.div
                        className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 1.8 }}
                    >
                        <img
                            src="/images/i.JPG"
                            alt="Identity Support"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                        />
                        <div className="absolute inset-0 bg-golden/10 rounded-bl-[100%]"></div>
                    </motion.div>
                </section>

                {/* Journey Exploration Section */}
                <div className="container mx-auto px-4 py-16">
                    <section className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                        <h2 className="text-3xl md:text-4xl text-center text-charcoal font-bold mb-12">
                            The Unconventional Path
                        </h2>

                        <div className="grid md:grid-cols-3 gap-8">
                            {journeySteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    className="bg-softWhite rounded-xl p-6 text-center transform hover:scale-105 transition-all"
                                >
                                    <div className="flex justify-center mb-4">
                                        {step.icon}
                                    </div>
                                    <h3 className="text-xl font-semibold text-charcoal mb-4">
                                        {step.title}
                                    </h3>
                                    <p className="text-charcol">
                                        {step.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </section>

                    {/* Vision Statement */}
                    <section className="mt-16 bg-golden-yellow bg-opacity-20 rounded-xl p-8 md:p-12 text-center">
                        <h2 className="text-3xl text-charcoal font-bold mb-6">
                            Our Therapeutic Philosophy
                        </h2>
                        <p className="text-xl text-slate-700 max-w-3xl mx-auto mb-8">
                            Therapists can fill others' cup when theirs is filled. At Greh Pravesh, we make it a point to create a therapist-centric space so that all our clients have the best experience possible. For this, we've adopted India's first no-revenue- sharing Group Practice Model.
                        </p>
                        <div className="flex justify-center">
                            <button onClick={() => navigate("/group-practice")} className="bg-golden text-charcol px-8 py-4 rounded-lg hover:opacity-90 transition-colors flex items-center">
                                Learn More
                                <ArrowRightCircle className="ml-3" />
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default AboutPage;